import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  Search,
  Button,
  InlineLoading,
  InlineNotification,
  TextInput,
  TextArea,
  ToastNotification,
  Modal,
  Tag,
  Select,
  SelectItem,
  SkeletonPlaceholder,
  SkeletonText,
  FileUploader
} from '@carbon/react';
import { Add, TrashCan, ChevronLeft, ChevronRight, Close } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import PageContainer from '../components/PageContainer';
import { usePermissions } from '../contexts/PermissionContext';
import './Meals.css';

const Meals = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';

  // Stranica „Obroci" je vidljiva zaposlenima koji imaju dozvolu za ovaj
  // modul, ali dodavanje i brisanje obroka zahteva `meals.manage`
  // („Dodavanje/brisanje obroka za jelovnike") - isto što traži i backend
  // za POST/PUT/DELETE /api/Meal.
  const { can } = usePermissions();
  const canManageMeals = can('meals.manage');

  // Da li je desktop viewport (>= 769px). Na mobilnom (< 769px) paginacija ide
  // ispod liste, a dugme "Dodaj obrok" je lebdeći (FAB) u donjem desnom uglu.
  const [isDesktopView, setIsDesktopView] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 769px)').matches
      : true
  );

  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  // Server-side pageSize (API dozvoljava 1-100; 6 odgovara grid-u od 3 kolone).
  const pageSize = 6;
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  // Pretraga se šalje serveru (debounce 300 ms) — sa server-side paginacijom
  // filtriranje po stranici ne bi vratilo rezultate sa ostalih stranica.
  const [searchQuery, setSearchQuery] = useState('');
  const [pageFading, setPageFading] = useState(false);
  
  // Side panel state for adding/editing meals
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    imgUrl: '',
    description: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [mealToDelete, setMealToDelete] = useState(null);
  
  // View details side panel state
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [viewingMeal, setViewingMeal] = useState(null);
  const [isMealImageModalOpen, setIsMealImageModalOpen] = useState(false);

  // Učitaj podatke sa API-ja (server-side paginacija — traži se tačna stranica)
  const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);

        const mealsResponse = await axiosInstance.get('/api/Meal', {
          params: {
            pageNumber: page,
            pageSize,
            ...(searchQuery ? { search: searchQuery } : {})
          }
        });

        // API vraća PagedResponse: { success, data: [...], pagination: {...} }
        const body = mealsResponse.data;
        const data = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body)
            ? body
            : [];

        setMeals(data);
        setTotalCount(body?.pagination?.totalCount ?? data.length);
      } catch (err) {
        console.error('Greška pri učitavanju podataka:', err);
        setError('Nije moguće učitati obroke. Pokušajte ponovo.');
        setMeals([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounce pretrage (300 ms) — svaki otkucaj ne pravi zahtev, a pretraga ide na
  // server, pa se vraća na prvu stranicu rezultata.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchQuery(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Prati promenu širine viewporta (mobilni <-> desktop raspored)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(min-width: 769px)');
    const handleChange = (event) => setIsDesktopView(event.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Stariji browseri (Safari < 14)
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  const handleDeleteMeal = async (id, e) => {
    e.stopPropagation();
    // Brisanje obroka zahteva dozvolu `meals.manage`.
    if (!canManageMeals) return;
    const meal = meals.find(m => m.id === id);
    setMealToDelete(meal);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!canManageMeals || !mealToDelete) return;
    
    try {
      await axiosInstance.delete(`/api/Meal/${mealToDelete.id}`);

      // Server-side paginacija: osveži stranicu; ako je bila poslednja stavka na
      // stranici > 1, vrati se na prethodnu.
      if (meals.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchData();
      }
      
      // Prikaži toast notifikaciju
      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: `Obrok "${mealToDelete.name}" je uspešno obrisan.`
      });
      
      // Automatski sakrij toast nakon 3 sekunde
      setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 3000);
    } catch (err) {
      console.error('Greška pri brisanju obroka:', err);
      setError('Nije moguće obrisati obrok. Pokušajte ponovo.');
      
      // Prikaži toast notifikaciju za grešku
      setToast({
        show: true,
        kind: 'error',
        title: 'Greška!',
        subtitle: 'Nije moguće obrisati obrok. Pokušajte ponovo.'
      });
      
      // Automatski sakrij toast nakon 4 sekunde
      setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 4000);
    } finally {
      setIsDeleteModalOpen(false);
      setMealToDelete(null);
    }
  };

  const handleEditMeal = (meal, e) => {
    e.stopPropagation();
    // Izmena obroka zahteva dozvolu `meals.manage`.
    if (!canManageMeals) return;
    // TODO: Implement edit functionality
    console.log('Edit meal:', meal);
  };

  const openViewPanel = (meal) => {
    setViewingMeal(meal);
    setIsViewPanelOpen(true);
  };

  const closeViewPanel = () => {
    setIsViewPanelOpen(false);
    setViewingMeal(null);
    setIsMealImageModalOpen(false);
  };

  const nextPage = () => {
    if (page < totalPages) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setPageFading(false);
      }, 300);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev - 1);
        setPageFading(false);
      }, 300);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openSidePanel = () => {
    // Dodavanje obroka zahteva dozvolu `meals.manage`.
    if (!canManageMeals) return;
    // Resetuj formu sa default vrednostima
    setFormData({
      name: '',
      imgUrl: '',
      description: ''
    });
    setImageFile(null);
    
    setIsSidePanelOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManageMeals) return;
    setFormLoading(true);
    setFormError(null);

    // Validacija
    if (!formData.name) {
      setFormError('Naziv obroka je obavezan.');
      setFormLoading(false);
      return;
    }

    try {
      // Kreiraj obrok
      const payload = {
        name: formData.name,
        imgUrl: formData.imgUrl || '',
        description: formData.description || ''
      };
      
      const response = await axiosInstance.post('/api/Meal', payload);
      console.log('Novi obrok kreiran:', response.data);
      
      // Dodaj novi obrok u listu
      const newMeal = response.data.data || response.data;
      const mealId = newMeal.id || newMeal.mealId;
      
      // Ako je izabrana slika, upload-uj je
      if (imageFile && mealId) {
        setIsImageUploading(true);
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', imageFile);
          await axiosInstance.post(`/api/Meal/${mealId}/upload-image`, uploadFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadErr) {
          console.error('Greška pri uploadu slike:', uploadErr);
        } finally {
          setIsImageUploading(false);
        }
      }
      
      setMeals(prev => [...prev, newMeal]);
      
      // Resetuj formu i zatvori panel
      setFormData({ name: '', imgUrl: '', description: '' });
      setImageFile(null);
      setIsSidePanelOpen(false);
      
      // Prikaži toast notifikaciju
      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: `Obrok "${formData.name}" je uspešno dodat.`
      });
      
      // Osveži listu bez punog reload-a stranice (reload bi na mobilnom
      // ponovo pokrenuo auth proveru i prikazao "Učitavanje..." ekran).
      fetchData();
    } catch (err) {
      console.error('Greška pri kreiranju obroka:', err);
      setFormError(err.response?.data?.message || 'Nije moguće kreirati obrok. Pokušajte ponovo.');
      
      // Prikaži toast notifikaciju za grešku
      setToast({
        show: true,
        kind: 'error',
        title: 'Greška!',
        subtitle: err.response?.data?.message || 'Nije moguće kreirati obrok. Pokušajte ponovo.'
      });
      
      // Automatski sakrij toast nakon 4 sekunde
      setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 4000);
    } finally {
      setFormLoading(false);
    }
  };

  // Pretraga je na serveru (`search` parametar, debounce iznad) — nema lokalnog
  // filtriranja, jer bi ono videlo samo učitanu stranicu.

  // Paginacija je na serveru — `meals` je već tražena stranica, a `totalCount`
  // dolazi iz `pagination.totalCount` (ukupan broj posle server-side pretrage).
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedMeals = meals;

  // Paginacija - prikazuje se u toolbaru (desktop) ili ispod liste (mobilni).
  // `extraClass` dodaje varijantu za prikaz ispod liste.
  const renderPagination = (extraClass = '') => (
    totalPages > 1 ? (
      <div
        className={`meals-pagination ${extraClass}`.trim()}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
      >
        <Button
          kind="ghost"
          size="md"
          renderIcon={ChevronLeft}
          onClick={prevPage}
          hasIconOnly
          disabled={page === 1}
          iconDescription="Prethodna stranica"
        />
        <span className="meals-pagination-label">
          {page}/{totalPages}
        </span>
        <Button
          kind="ghost"
          size="md"
          renderIcon={ChevronRight}
          onClick={nextPage}
          hasIconOnly
          disabled={page === totalPages}
          iconDescription="Sledeća stranica"
        />
      </div>
    ) : null
  );

  if (loading) {
    return (
      <PageContainer>
        <div className="meals-container">
          {/* Toolbar Skeleton */}
          <div className="meals-toolbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
              <SkeletonText style={{ width: '100%', maxWidth: '500px', height: '40px' }} />
            </div>
            {isDesktopView && <SkeletonText style={{ width: '140px', height: '40px' }} />}
          </div>

          {/* Grid Skeleton */}
          <div
            className="meals-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridTemplateRows: 'repeat(2, 1fr)',
              gap: '1rem',
              height: '100%',
              width: '100%'
            }}
          >
            {[...Array(6)].map((_, index) => (
              <SkeletonPlaceholder
                key={index}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '250px',
                  borderRadius: '0.75rem'
                }}
              />
            ))}
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {error && (
        <div style={{ 
          position: 'fixed', 
          top: '4rem', 
          right: '1rem', 
          zIndex: 9999,
          minWidth: '320px',
          animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <style>
            {`
              @keyframes slideInFromRight {
                from {
                  transform: translateX(calc(100% + 1rem));
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          <InlineNotification
            kind="error"
            title="Greška"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
      )}
      <div className="meals-container">

        {/* Toolbar */}
        <div className="meals-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
            <Search
              size="lg"
              placeholder="Pretražite obroke..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              onClear={() => {
                setSearchTerm('');
                setPage(1);
              }}
              labelText=""
              closeButtonLabelText="Obriši pretragu"
              style={{ flex: 1 }}
            />
            {/* Paginacija u toolbaru - samo desktop (na mobilnom ide ispod liste) */}
            {isDesktopView && renderPagination()}
          </div>
          {/* Dugme "Dodaj obrok" - samo desktop (na mobilnom je lebdeći FAB) */}
          {canManageMeals && isDesktopView && (
            <Button
              kind="primary"
              renderIcon={Add}
              onClick={openSidePanel}
              className="add-meal-button"
            >
              Dodaj obrok
            </Button>
          )}
        </div>

        {/* Grid */}
        {paginatedMeals.length === 0 ? (
          <div className="meals-empty">
            <div className="empty-icon">🍽️</div>
            <p className="empty-title">Nema obroka</p>
            <p className="empty-subtitle">
              {searchTerm ? 'Pokušajte sa drugom pretragom' : 'Dodajte prvi obrok'}
            </p>
          </div>
        ) : (
          <div 
            className="meals-grid"
            style={{
              opacity: pageFading ? 0 : 1,
              transition: 'opacity 0.3s ease',
              width: '100%'
            }}
          >
            {paginatedMeals.map((meal) => (
              <div 
                key={meal.id} 
                className="meal-card"
                style={{
                  backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%), url(${meal.imgUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  height: '100%',
                  width: '100%',
                  position: 'relative',
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  border: '1px solid var(--cds-border-subtle, #e0e0e0)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => openViewPanel(meal)}
              >
                {/* Actions - positioned at top right */}
                {canManageMeals && (
                  <div className="meal-card-actions">
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      hasIconOnly
                      onClick={(e) => handleDeleteMeal(meal.id, e)}
                      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                    />
                  </div>
                )}

                {/* Content - positioned at bottom */}
                <div className="meal-card-content">
                  <h3 className="meal-card-name">
                    {meal.name}
                  </h3>
                  {meal.createdAt && (
                    <p className="meal-card-date">
                      Dodato: {new Date(meal.createdAt).toLocaleDateString('sr-RS')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Mobilni: paginacija ispod liste */}
        {!isDesktopView && paginatedMeals.length > 0 && renderPagination('meals-pagination--bottom')}

        {/* Right Side Panel - add only, hidden for caregivers */}
        {canManageMeals && (
        <div className={`meal-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h2>Dodaj obrok</h2>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              onClick={() => setIsSidePanelOpen(false)}
            />
          </div>
          {formError && (
            <div style={{ 
              position: 'fixed', 
              top: '4rem', 
              right: '1rem', 
              zIndex: 9999,
              minWidth: '320px',
              animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <style>
                {`
                  @keyframes slideInFromRight {
                    from {
                      transform: translateX(calc(100% + 1rem));
                      opacity: 0;
                    }
                    to {
                      transform: translateX(0);
                      opacity: 1;
                    }
                  }
                `}
              </style>
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={formError}
                onCloseButtonClick={() => setFormError(null)}
                hideCloseButton={false}
                lowContrast
              />
            </div>
          )}
          <div className="side-panel-content">

            <form onSubmit={handleSubmit} className="meal-form">
              <TextInput
                id="name"
                labelText="Naziv obroka"
                placeholder="Unesite naziv obroka"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                disabled={formLoading}
              />

              <div style={{ marginBottom: '1rem' }}>
                <label className="cds--label" style={{ marginBottom: '0.5rem', display: 'block' }}>
                  Slika obroka
                </label>
                <FileUploader
                  labelTitle=""
                  labelDescription="Samo JPG, PNG, WEBP, GIF, BMP (maks 5MB)"
                  buttonLabel="Izaberi sliku"
                  filenameStatus="edit"
                  accept={['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp']}
                  multiple={false}
                  disabled={formLoading || isImageUploading}
                  onChange={(e) => setImageFile(e.target.files[0])}
                />
                {isImageUploading && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                    Upload slike u toku...
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <TextArea
                  id="description"
                  labelText="Opis obroka"
                  placeholder="Unesite opis obroka (opciono)"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  disabled={formLoading}
                  rows={4}
                />
              </div>

              <div className="form-actions">
                <Button
                  kind="secondary"
                  onClick={() => setIsSidePanelOpen(false)}
                  disabled={formLoading}
                >
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={formLoading}
                >
                  {formLoading ? 'Čuvanje...' : 'Sačuvaj'}
                </Button>
              </div>
            </form>
          </div>
        </div>
        )}

        {/* Overlay */}
        {isSidePanelOpen && (
          <div 
            className="side-panel-overlay" 
            onClick={() => setIsSidePanelOpen(false)}
          />
        )}

        {/* View Details Side Panel */}
        <div className={`meal-side-panel ${isViewPanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h2>Detalji obroka</h2>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              onClick={closeViewPanel}
            />
          </div>

          {viewingMeal && (
            <div className="side-panel-content" style={{ padding: '1.5rem' }}>
              {/* Slika obroka */}
              {viewingMeal.imgUrl && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <img
                    src={viewingMeal.imgUrl}
                    alt={viewingMeal.name || 'Obrok'}
                    style={{
                      width: '100%',
                      maxHeight: '260px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid var(--cds-border-subtle, #e0e0e0)',
                      backgroundColor: 'var(--cds-layer-02, #f4f4f4)',
                      cursor: 'zoom-in'
                    }}
                    onClick={() => setIsMealImageModalOpen(true)}
                  />
                </div>
              )}

              {/* Naziv obroka */}
              <h3 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '600', 
                marginBottom: '1rem',
                color: 'var(--cds-text-primary)'
              }}>
                {viewingMeal.name}
              </h3>

              {/* Osnovne informacije */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                {viewingMeal.description && (
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.75rem', 
                      fontWeight: '600',
                      color: 'var(--cds-text-secondary)',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Opis
                    </label>
                    <div style={{ fontSize: '1rem', lineHeight: '1.5' }}>
                      {viewingMeal.description}
                    </div>
                  </div>
                )}

                {viewingMeal.createdAt && (
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.75rem', 
                      fontWeight: '600',
                      color: 'var(--cds-text-secondary)',
                      marginBottom: '0.25rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Datum dodavanja
                    </label>
                    <div style={{ fontSize: '1rem' }}>
                      {new Date(viewingMeal.createdAt).toLocaleDateString('sr-RS')}
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {canManageMeals && (
                  <Button 
                    kind="danger" 
                    size="md"
                    style={{ flex: 1 }}
                    onClick={(e) => {
                      closeViewPanel();
                      handleDeleteMeal(viewingMeal.id, e);
                    }}
                  >
                    Obriši obrok
                  </Button>
                )}
                <Button 
                  kind="secondary" 
                  size="md"
                  style={{ flex: 1 }}
                  onClick={closeViewPanel}
                >
                  Zatvori
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Overlay for View Panel */}
        {isViewPanelOpen && (
          <div 
            className="side-panel-overlay" 
            onClick={closeViewPanel}
          />
        )}

        {/* Meal Image Modal */}
        {isMealImageModalOpen && (
          <Modal
            open
            onRequestClose={() => setIsMealImageModalOpen(false)}
            onSecondarySubmit={() => setIsMealImageModalOpen(false)}
            modalHeading={viewingMeal?.name || 'Obrok'}
            size="sm"
            passiveModal
          >
            {viewingMeal?.imgUrl && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '0.5rem 0'
                }}
              >
                <img
                  src={viewingMeal.imgUrl}
                  alt={viewingMeal.name || 'Obrok'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '60vh',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              </div>
            )}
          </Modal>
        )}

        {/* Toast Notification */}
        {toast.show && (
          <div className="toast-container">
            <ToastNotification
              kind={toast.kind}
              title={toast.title}
              subtitle={toast.subtitle}
              timeout={0}
              onClose={() => setToast(prev => ({ ...prev, show: false }))}
              lowContrast
            />
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          open={isDeleteModalOpen}
          onRequestClose={() => setIsDeleteModalOpen(false)}
          modalHeading="Brisanje obroka"
          primaryButtonText="Obriši"
          secondaryButtonText="Otkaži"
          onRequestSubmit={confirmDelete}
          danger
          size="sm"
        >
          <p>
            Da li ste sigurni da želite da obrišete obrok{' '}
            <strong>{mealToDelete?.name}</strong>?
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
            Ova akcija se ne može poništiti.
          </p>
        </Modal>

        {/* Floating Action Button - Dodaj obrok (mobile only) */}
        {canManageMeals && !isSidePanelOpen && !isViewPanelOpen && ReactDOM.createPortal(
          <button
            className="floating-button meal-fab"
            onClick={openSidePanel}
            style={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              height: '56px',
              padding: '0 24px',
              backgroundColor: '#9fb746',
              border: 'none',
              borderRadius: '28px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              gap: '12px',
              outline: 'none',
              zIndex: 9999
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8da63d';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#9fb746';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
            }}
          >
            <Add
              size={24}
              style={{
                flexShrink: 0,
                minWidth: '24px',
                minHeight: '24px',
                fill: '#ffffff'
              }}
              aria-label="Dodaj obrok"
              color="#ffffff"
            />
            <span style={{
              color: 'white',
              fontWeight: '500',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}>
              Dodaj obrok
            </span>
          </button>,
          document.body
        )}

      </div>
    </PageContainer>
  );
};

export default Meals;
